import React from 'react';
import { Activity, Calculator } from 'lucide-react';
import Button from '../../../../../components/ui/Button';
import { formatNumber } from '../../../../../utils/formatting';

/**
 * Компонент боковой панели для быстрой настройки параметров
 */
const QuickAdjustmentSidebar = ({ 
  formData,
  setFormData,
  errors,
  isCalculating
}) => {
  // Обработчик изменения веса
  const handleWeightChange = (e) => {
    const newValue = e.target.value;
    setFormData(prev => ({
      ...prev,
      weight: newValue
    }));
  };
  
  // Обработчик изменения роста
  const handleHeightChange = (e) => {
    const newValue = e.target.value;
    setFormData(prev => ({
      ...prev,
      height: newValue
    }));
  };
  
  // Обработчик изменения возраста
  const handleAgeChange = (e) => {
    const newValue = e.target.value;
    setFormData(prev => ({
      ...prev,
      age: newValue
    }));
  };
  
  // Обработчик изменения окружности талии
  const handleWaistChange = (e) => {
    const newValue = e.target.value;
    setFormData(prev => ({
      ...prev,
      waist: newValue
    }));
  };
  
  // Предопределенные методы измерения
  const predefinedMethods = [
    { id: 'navy', label: 'ВМС США' },
    { id: 'jackson_pollock_3', label: 'Калипер 3' },
    { id: 'jackson_pollock_7', label: 'Калипер 7' }
  ];
  
  // Расчет ИМТ для отображения
  const calculateBMI = () => {
    const weight = parseFloat(formData.weight);
    const height = parseFloat(formData.height);
    
    if (isNaN(weight) || isNaN(height) || height <= 0) {
      return null;
    }
    
    const bmi = weight / Math.pow(height / 100, 2);
    return bmi;
  };
  
  const bmi = calculateBMI();
  
  // Категории ИМТ
  const getBMICategory = (bmi) => {
    if (bmi < 18.5) return { category: 'Недостаточная масса тела', color: 'text-blue-500' };
    if (bmi < 25) return { category: 'Нормальная масса тела', color: 'text-green-500' };
    if (bmi < 30) return { category: 'Избыточная масса тела', color: 'text-yellow-500' };
    if (bmi < 35) return { category: 'Ожирение I степени', color: 'text-orange-500' };
    if (bmi < 40) return { category: 'Ожирение II степени', color: 'text-red-500' };
    return { category: 'Ожирение III степени', color: 'text-red-700' };
  };
  
  const bmiInfo = bmi ? getBMICategory(bmi) : null;
  
  // Функция расчета
  const calculateBodyFat = () => {
    // Вызов кнопки расчета в основной форме
    document.querySelector('button[type="submit"]')?.click();
  };
  
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <Calculator className="w-5 h-5 mr-2 text-emerald-500" />
        Быстрая настройка
      </h3>
      
      <div className="space-y-5">
        {/* Метод измерения */}
        <div>
          <div className="text-sm text-gray-600 dark:text-gray-400 mb-2">
            Метод измерения
          </div>
          <div className="grid grid-cols-3 gap-1">
            {predefinedMethods.map(method => (
              <button
                key={method.id}
                type="button"
                onClick={() => setFormData(prev => ({ 
                  ...prev, 
                  measurementMethod: method.id,
                  bodyfatSkinfold: method.id !== 'navy'
                }))}
                className={`px-2 py-1.5 text-xs rounded border transition-colors
                  ${formData.measurementMethod === method.id 
                    ? 'bg-emerald-100 border-emerald-300 text-emerald-700 dark:bg-emerald-900/30 dark:border-emerald-700 dark:text-emerald-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
              >
                {method.label}
              </button>
            ))}
          </div>
        </div>
        
        {/* Пол */}
        <div>
          <div className="text-sm text-gray-600 dark:text-gray-400 mb-2">
            Пол
          </div>
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, gender: 'male', hip: '0' }))}
              className={`p-2 text-sm rounded-lg border transition-colors
                ${formData.gender === 'male' 
                  ? 'bg-emerald-100 border-emerald-300 text-emerald-700 dark:bg-emerald-900/30 dark:border-emerald-700 dark:text-emerald-300'
                  : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
            >
              <div className="font-medium">Мужской</div>
            </button>
            <button
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, gender: 'female', hip: prev.hip || '95' }))}
              className={`p-2 text-sm rounded-lg border transition-colors
                ${formData.gender === 'female' 
                  ? 'bg-emerald-100 border-emerald-300 text-emerald-700 dark:bg-emerald-900/30 dark:border-emerald-700 dark:text-emerald-300'
                  : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
            >
              <div className="font-medium">Женский</div>
            </button>
          </div>
        </div>
        
        {/* Вес */}
        <div>
          <div className="flex justify-between items-center mb-1 text-sm">
            <label htmlFor="weightSlider" className="text-gray-600 dark:text-gray-400">Вес (кг)</label>
            <span className="font-medium text-gray-900 dark:text-white">{formData.weight} кг</span>
          </div>
          <input
            type="range"
            id="weightSlider"
            min="40"
            max="150"
            step="1"
            value={formData.weight}
            onChange={handleWeightChange}
            className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-emerald-600"
          />
          
          {/* Предопределенные значения веса */}
          <div className="grid grid-cols-4 gap-1 mt-2">
            {['50', '70', '90', '110'].map(value => (
              <button
                key={value}
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, weight: value }))}
                className={`px-2 py-1 text-xs rounded border transition-colors
                  ${formData.weight === value 
                    ? 'bg-emerald-100 border-emerald-300 text-emerald-700 dark:bg-emerald-900/30 dark:border-emerald-700 dark:text-emerald-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
              >
                {value} кг
              </button>
            ))}
          </div>
        </div>
        
        {/* Рост */}
        <div>
          <div className="flex justify-between items-center mb-1 text-sm">
            <label htmlFor="heightSlider" className="text-gray-600 dark:text-gray-400">Рост (см)</label>
            <span className="font-medium text-gray-900 dark:text-white">{formData.height} см</span>
          </div>
          <input
            type="range"
            id="heightSlider"
            min="150"
            max="200"
            step="1"
            value={formData.height}
            onChange={handleHeightChange}
            className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-emerald-600"
          />
          
          {/* Предопределенные значения роста */}
          <div className="grid grid-cols-4 gap-1 mt-2">
            {['160', '170', '180', '190'].map(value => (
              <button
                key={value}
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, height: value }))}
                className={`px-2 py-1 text-xs rounded border transition-colors
                  ${formData.height === value 
                    ? 'bg-emerald-100 border-emerald-300 text-emerald-700 dark:bg-emerald-900/30 dark:border-emerald-700 dark:text-emerald-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
              >
                {value} см
              </button>
            ))}
          </div>
        </div>
        
        {/* Индекс массы тела (ИМТ) */}
        {bmi && (
          <div className="p-3 bg-gray-50 dark:bg-gray-700/30 rounded-lg">
            <div className="text-xs text-gray-600 dark:text-gray-400 mb-1">Индекс массы тела (ИМТ)</div>
            <div className="font-medium text-lg mb-1">
              {formatNumber(bmi, 1)} кг/м²
            </div>
            <div className={`text-sm ${bmiInfo?.color}`}>
              {bmiInfo?.category}
            </div>
          </div>
        )}
        
        {/* Кнопка расчета */}
        <Button
          primary
          fullWidth
          onClick={calculateBodyFat}
          icon={<Activity className="w-4 h-4" />}
          disabled={isCalculating || Object.keys(errors).length > 0}
          isLoading={isCalculating}
        >
          Рассчитать процент жира
        </Button>
      </div>
    </div>
  );
};

export default QuickAdjustmentSidebar;